import { createRestClient } from '@superbet-group/web.lib.rest';
import { getShellConfig } from '@src/config/shell/config';
import { useSurveyRenderManager } from './SurveyRenderManager';
import {
    SurveyFieldType,
    FormConfiguration,
} from './FormConfiguration';
import { SurveyResponseDto } from './SurveyRemoteSourceDtos';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { SurveyProtoRequestDto } from './SurveyProtoRequest';
import { getDeviceId } from '@src/config/idGenerationService';
import {
    buildBundleId,
    getAppVersion,
    getPlatform,
} from '@src/app/utils/AnalyticsUtils';
import { logger } from '@src/app/logger';
import { SpecificSurveyPageSetup } from '@src/config/shell/remoteConfigTypes';

const surveyBaseSetup = getShellConfig().remoteConfig.surveyBaseSetup;

const defaultTimeoutMs = 6000;
export interface ISurveyService {
  fetchSurveyData(): Promise<FormConfiguration | null>;
  submitSurvey(startTimestamp: string, formConfiguration: FormConfiguration, formValues: Record<string, string | string[]>): Promise<void>;
}

export function useSurveyService(surveyPageSetup: SpecificSurveyPageSetup, surveyId: string, userId: string): ISurveyService {
    const surveysApi = createRestClient(surveyBaseSetup?.surveysContentBaseUrl ?? '', defaultTimeoutMs);
    const analyticsApi = createRestClient(surveyBaseSetup?.analyticsApiBaseUrl ?? '', defaultTimeoutMs);
    const analyticsSDKConfig = AnalyticsSDK.getInstance().getConfig();

    async function fetchSurveyData(): Promise<FormConfiguration | null> {
        try {
            const response = await surveysApi.get<SurveyResponseDto>(`/cached-superbet/surveys-api/surveys/${surveyId}`);
            return mapSurveyToFormData(response);
        } catch (e) {
            logger.error(e as Error);
            return null;
        }
    }

    function mapSurveyToFormData(survey: SurveyResponseDto): FormConfiguration {
        const formConfiguration: FormConfiguration = {
            activeTo: survey.active_to,
            activeFrom: survey.active_from,
            active: survey.active,
            fields: survey.questions.map((question) => ({
                id: question.id,
                lokKey: question.lok_key,
                type: question.type as SurveyFieldType,
                default: '',
                options: question.options?.map((option) => ({
                    id: option.id,
                    lokKey: option.lok_key,
                })),
                required: question.required,
                multi: question.multi,
            })),
        };
        return formConfiguration;
    }

    function mapJsonToProtobuffer(formConfiguration: FormConfiguration, formValues: Record<string, string | string[]>): SurveyProtoRequestDto {
        return {
            id: surveyId,
            answers: formConfiguration.fields.map((question) => {
                const answerValue = formValues[question.id];
                let values: string[] = [];
                if (Array.isArray(answerValue)) {
                    values = answerValue.map((val) => val.toString());
                } else if (answerValue !== undefined && answerValue !== null) {
                    values = [answerValue.toString()];
                }

                return {
                    id: question.id,
                    values: values,
                };
            }),
        };
    }

    async function encodeSurvey(surveyData: SurveyProtoRequestDto): Promise<Uint8Array> {
        const { parse } = (await import('protobufjs/minimal'));
        const protoDefinition = (await import('@store/surveysProtobufSchema')).default;
        const root = parse(protoDefinition).root;
        const Survey = root.lookupType('surveys.Survey');
        return Survey.encode(surveyData).finish();
    }

    async function submitSurvey(startTimestamp: string, formConfiguration:FormConfiguration, formValues: Record<string, string>): Promise<void> {
        try {
            const base64UserData = btoa(
                JSON.stringify({
                    start_time_stamp: startTimestamp,
                    end_time_stamp: Date.now().toString(),
                    user_id: userId.toString(),
                    installation_id: getDeviceId(),
                    platform: getPlatform(),
                    version: getAppVersion(analyticsSDKConfig),
                    bundle_id: buildBundleId(analyticsSDKConfig),
                }));
            const surveyProto = mapJsonToProtobuffer(formConfiguration, formValues);
            const buffer = await encodeSurvey(surveyProto);
            await analyticsApi.post('kolektor/ankete', String.fromCharCode(...buffer), {
                headers: {
                    ContentType: 'application/protobuf',
                    'Survey-Metadata': base64UserData,
                },
            });
            useSurveyRenderManager(surveyId ?? '', userId, surveyPageSetup).setHasAnswered();
        } catch (e) {
            logger.error(e as Error);
        }
    }

    return {
        fetchSurveyData,
        submitSurvey,
    };
}
